import { faBuilding, faShareNodes } from '@fortawesome/pro-light-svg-icons';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { SlcpCompanyListCard } from '../-components/SlcpCompanyListCard';
import { SlcpDateRangeFilter } from '../-components/SlcpDateRangeFilter';
import { SlcpHelperStatsCard } from '../-components/SlcpHelperStatsCard';
import { SlcpMetricDefinitions } from '../-components/SlcpMetricDefinitions';
import { SlcpMetricsGrid } from '../-components/SlcpMetricsGrid';
import { slcpAnalyticsQueryOptions } from '../-hooks/useSlcpAnalytics';
import { useSlcpDashboardPage } from '../-hooks/useSlcpDashboardPage';

/**
 * Both bounds optional — no range at all is the default view, "all time".
 * Anything unparseable is dropped rather than carried through: the endpoint's
 * querystring schema is `format: 'date-time'` and answers 400 to the rest, and
 * a hand-edited URL should degrade to all-time, not to an error page.
 */
const isoDateTime = z
  .string()
  .refine(v => !Number.isNaN(new Date(v).getTime()))
  .optional()
  .catch(undefined);

const slcpDashboardSearch = z.object({
  from: isoDateTime,
  to: isoDateTime,
});

export const Route = createFileRoute('/management/slcp-dashboard/(pages)/')({
  validateSearch: slcpDashboardSearch,
  loaderDeps: ({ search }) => ({
    ...(search.from !== undefined ? { from: search.from } : {}),
    ...(search.to !== undefined ? { to: search.to } : {}),
  }),
  // Swallowed: a rejected loader hands the route to the router's error
  // boundary, and the page already reports a failed load inline.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(slcpAnalyticsQueryOptions(deps))
      .catch(() => undefined),
  component: SlcpDashboardPage,
});

function SlcpDashboardPage() {
  const { getTranslation: t } = useTranslator({
    page: 'managementSlcpDashboard',
  });
  const page = useSlcpDashboardPage();

  // `overflow-y-auto!`: `PageContainer` ships `overflow-hidden`, and the
  // shorthand and the axis utility are separate groups to `twMerge`, so both
  // survive the merge and only `!important` settles which one applies.
  return (
    <PageContainer className='overflow-y-auto! gap-0 py-0!'>
      {/* Sticky: the range applies to every number below it, so it stays in
          view while the page scrolls. It owns the page's top padding — hence
          `py-0!` on the container — because a padding band above a sticky child
          is a gap the child cannot cover once it is stuck. The negative inline
          margins bleed its background out to the container's side padding, or
          content shows through beside it. */}
      <div className='sticky top-0 z-10 shrink-0 -mx-4 flex flex-col gap-3 bg-background px-4 py-3 min-[1600px]:-mx-6 min-[1600px]:px-6 min-[1600px]:py-4'>
        <p className='text-xs text-muted-foreground'>{t('subtitle')}</p>

        <SlcpDateRangeFilter
          range={page.range}
          label={page.rangeLabel}
          onRangeChange={page.setRange}
          onQuickRange={page.setQuickRange}
          onClear={page.clearRange}
        />
      </div>

      {/* One `shrink-0` block rather than loose cards: `Card` carries
          `overflow-hidden`, which zeroes a flex item's automatic minimum size,
          so as direct children of the scrolling column the cards would shrink
          and clip their own content instead of overflowing it. */}
      <div className='shrink-0 flex flex-col gap-4 pb-4'>
        {page.isError ? (
          <QueryErrorState
            error={page.error}
            onRetry={() => void page.refetch()}
            title={t('error.title')}
          />
        ) : (
          <>
            <SlcpMetricsGrid metrics={page.metrics} loading={page.isLoading} />

            {page.companies.length > 0 && (
              <SlcpCompanyListCard
                title={t('companies.title')}
                icon={faBuilding}
                tone='companies'
                countLabel={t('companies.countLabel')}
                companies={page.companies}
              />
            )}

            {page.sharedWithCompanies.length > 0 && (
              <SlcpCompanyListCard
                title={t('sharedCompanies.title')}
                icon={faShareNodes}
                tone='shared'
                countLabel={t('sharedCompanies.countLabel')}
                companies={page.sharedWithCompanies}
              />
            )}

            <SlcpHelperStatsCard
              title={t('helper.title')}
              stats={page.helperStats}
              loading={page.isLoading}
            />

            <SlcpMetricDefinitions />
          </>
        )}
      </div>
    </PageContainer>
  );
}
